import { extractMetadata, extractMessages, extractGrounding } from './extractors';
import { 
  pushHeading, 
  codeFence, 
  collapseWhitespace, 
  hardWrap,
  safeYaml,
  stringifyIfNotString
} from './markdown';

/**
 * Converts Google AI Studio JSON export to Markdown
 * @param {Object} root - The JSON root object
 * @param {Object} opts - Conversion options
 * @returns {Object} - { md: string, stats: object }
 */
export function jsonToMarkdown(root, opts) {
  const meta = extractMetadata(root);
  const messages = extractMessages(root, opts.includeHiddenThoughts);
  const refs = opts.includeGrounding ? extractGrounding(root) : [];

  const lines = [];

  // YAML frontmatter
  if (opts.includeFrontmatter) {
    lines.push("---");
    const title = safeYaml(meta.title || inferTitle(meta, messages));
    const date = safeYaml(meta.date || new Date().toISOString());
    lines.push(`title: ${title}`);
    lines.push(`date: ${date}`);
    if (opts.includeRunSettings && meta.runSettings) {
      const rs = meta.runSettings;
      if (rs.model) lines.push(`model: ${safeYaml(rs.model)}`);
      if (typeof rs.temperature === "number") lines.push(`temperature: ${rs.temperature}`);
      if (typeof rs.topP === "number") lines.push(`top_p: ${rs.topP}`);
      if (typeof rs.topK === "number") lines.push(`top_k: ${rs.topK}`);
      if (typeof rs.maxOutputTokens === "number") lines.push(`max_output_tokens: ${rs.maxOutputTokens}`);
    }
    lines.push("---\n");
  }

  // Optional: system instruction
  if (opts.includeSystemInstruction && meta.systemInstruction) {
    pushHeading(lines, "System instruction", 2, opts.headingStyle);
    lines.push(codeFence("json", stringifyIfNotString(meta.systemInstruction)));
    lines.push("");
  }

  // Conversation
  pushHeading(lines, "Conversation", 2, opts.headingStyle);
  for (const m of messages) {
    const roleLabel = roleToLabel(m.role);
    if (opts.roleHeadings) pushHeading(lines, roleLabel, 3, opts.headingStyle);
    lines.push(m.text);
    lines.push("");
  }

  // Grounding / references
  if (refs.length) {
    pushHeading(lines, "References", 2, opts.headingStyle);
    refs.forEach((r, i) => {
      lines.push(`- [${i + 1}] ${r.title ? r.title + " — " : ""}${r.uri}`);
    });
    lines.push("");
  }

  let md = lines.join("\n");
  if (opts.collapseWhitespace) md = collapseWhitespace(md);
  if (opts.wrapWidth > 0) md = hardWrap(md, opts.wrapWidth);

  const stats = {
    messageCount: messages.length,
    thoughtCount: messages.filter((m) => m.isThought).length,
    hasGrounding: refs.length > 0,
  };

  return { md, stats };
}

/**
 * Converts role string to display label
 */
function roleToLabel(role) {
  const normalized = normalizeRole(role);
  switch (normalized) {
    case "user":
      return "User";
    case "assistant":
      return "Assistant";
    case "system":
      return "System";
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
}

/**
 * Normalizes role names from API
 */
function normalizeRole(role) {
  if (!role) return "assistant";
  const r = String(role).toLowerCase();
  if (r === "model") return "assistant";
  if (r === "user") return "user";
  return r;
}

/**
 * Infers a title from messages if not present in metadata
 */
function inferTitle(meta, messages) {
  // Use first user message snippet as title fallback
  const firstUser = messages.find((m) => m.role === "user");
  if (firstUser && firstUser.text) {
    const t = firstUser.text.split(/\n/)[0].trim();
    return t.slice(0, 80) + (t.length > 80 ? "…" : "");
  }
  return "AI Studio Conversation";
}
